import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { DialogSize } from '@rio-cloud/rio-uikit/components/dialog/Dialog';
import { OPEN_DIALOG_EVENT } from '@rio-cloud/rio-uikit/FrameDialog';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

import { config } from '@/config';
import { DEFAULT_LOCALE } from '@/configuration/lang/lang';
import { getLocale } from '@/configuration/lang/langSlice';
import { useAppSelector } from '@/configuration/setup/hooks';
import { getAccessToken } from '@/configuration/tokenHandling/tokenSlice';
import { sendMessage } from '@/features/app/MessageHandler';
import { CONFIRM_DIALOG_PATH, MFE_DIALOG_PREFIX } from '@/features/common/components/dialogs/dialogConstants';
import {
    CONFIRM_BROADCAST_CHANNEL,
    ConfirmResultPayload,
    ConfirmType,
} from '@/features/maintenance-data/confirmMessages';
import { UseRowSelection } from '@/features/maintenance-data/hooks/useRowSelection';
import { COMMON_SHOW_NOTIFICATION, ShowNotificationPayload } from '@/features/maintenance-data/notificationMessages';
import { usePatchMaintenanceComponentMutation } from '@/services/vehicleDiagnosticsApi';

export interface UseMarkSolvedActions {
    /** Opens the confirmation dialog for marking the current selection solved. */
    requestMarkSolved: () => void;
    /** Opens the confirmation dialog for reverting the current selection. */
    requestRevertSolved: () => void;
    /** True while the PATCH batch is in flight. */
    isUpdating: boolean;
}

/** `kind` → the `is_solved_by_customer` value to PATCH on confirm. */
const SOLVED_FLAG: Record<ConfirmType, boolean> = {
    markSolved: true,
    revertSolved: false,
};

const ERROR_MSG_KEY = 'intl-msg:vehicle-diagnostics.maintenance.table.error.markSolvedFailed';

const DIALOG_SIZE: DialogSize = 'md';

const buildConfirmUrl = (kind: ConfirmType, correlationId: string, locale: string, token: string) => {
    const url = new URL(config.mfeBaseUrl);
    url.pathname = CONFIRM_DIALOG_PATH;
    url.searchParams.set('kind', kind);
    url.searchParams.set('correlationId', correlationId);
    url.searchParams.set('wu_token', token);
    url.searchParams.set('wu_locale', locale);
    return url.toString();
};

/**
 * Confirm-gated bulk mark-solved / revert-solved actions for the maintenance-data table.
 *
 * `request*` opens a confirmation dialog (via FrameDialog in the parent WFE), snapshotting the
 * selected ids + target flag under a correlation id. The dialog broadcasts the user's decision on
 * {@link CONFIRM_BROADCAST_CHANNEL}; on a matching confirm we PATCH `{ is_solved_by_customer }` per
 * row, clear the selection, and let the `MaintenanceData` tag invalidation refetch the table.
 *
 * Success is silent (reflected by the status column after refetch); any PATCH failure shows an
 * error toast via the WFE (`COMMON/SHOW_NOTIFICATION`, already localized).
 */
export const useMarkSolvedActions = (selection: UseRowSelection): UseMarkSolvedActions => {
    const intl = useIntl();
    const { sendMessageToParent } = usePostMessageSender();
    const token = useAppSelector(getAccessToken);
    const locale = useAppSelector(getLocale) || DEFAULT_LOCALE;
    const [patch, { isLoading: isUpdating }] = usePatchMaintenanceComponentMutation();

    // Snapshot of what the open dialog is confirming: ids to patch, the target flag, and the
    // correlation id we expect back. Taken at request time so a late selection change can't alter
    // what the user confirmed.
    const pendingRef = useRef<{ correlationId: string; kind: ConfirmType; ids: string[] } | null>(null);

    const { clear, selectedItems } = selection;

    const applySolvedFlag = useCallback(
        async (ids: string[], isSolved: boolean) => {
            const results = await Promise.allSettled(
                ids.map((id) => patch({ id, body: { is_solved_by_customer: isSolved } }).unwrap()),
            );
            // The MaintenanceData tag invalidation refetches the table; clearing selection drops
            // any ids that no longer match. Success shows via the status column, so only surface failures.
            clear();

            if (results.some((result) => result.status === 'rejected')) {
                // VD owns i18n, so send already-localized text; WFE renders the toast.
                const payload: ShowNotificationPayload = {
                    type: 'ERROR',
                    description: intl.formatMessage({ id: ERROR_MSG_KEY }),
                };
                sendMessage({ type: COMMON_SHOW_NOTIFICATION, payload });
            }
        },
        [patch, clear, intl],
    );

    const openConfirm = useCallback(
        (kind: ConfirmType) => {
            const ids = selectedItems.map((item) => item.resource_id);
            if (ids.length === 0) {
                return;
            }
            const correlationId = crypto.randomUUID();
            pendingRef.current = { correlationId, kind, ids };

            sendMessageToParent({
                type: `${MFE_DIALOG_PREFIX}${OPEN_DIALOG_EVENT}`,
                payload: {
                    src: buildConfirmUrl(kind, correlationId, locale, token),
                    dialogProps: { bsSize: DIALOG_SIZE },
                },
            });
        },
        [selectedItems, locale, token, sendMessageToParent],
    );

    const requestMarkSolved = useCallback(() => openConfirm('markSolved'), [openConfirm]);
    const requestRevertSolved = useCallback(() => openConfirm('revertSolved'), [openConfirm]);

    // Listen for the dialog's decision on the broadcast channel. Match on correlation id, then run
    // the batch (confirmed) or just drop the pending snapshot (cancelled).
    useEffect(() => {
        const channel = new BroadcastChannel(CONFIRM_BROADCAST_CHANNEL);
        channel.onmessage = (event: MessageEvent<ConfirmResultPayload>) => {
            const payload = event.data;
            const pending = pendingRef.current;
            if (!payload || !pending || payload.correlationId !== pending.correlationId) {
                return;
            }
            const { ids, kind } = pending;
            pendingRef.current = null;
            if (payload.confirmed) {
                void applySolvedFlag(ids, SOLVED_FLAG[kind]);
            }
        };
        return () => channel.close();
    }, [applySolvedFlag]);

    return { requestMarkSolved, requestRevertSolved, isUpdating };
};
